import React from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, Box, Link } from '@material-ui/core';
import { MdOutlineEdit } from 'react-icons/md';
import { useCardStyles } from '../../styles';

/**
 * Componente base para tarjetas de métricas
 * Proporciona una estructura común reutilizable para cards de métricas con:
 * - Header con título, subtítulo e icono de edición
 * - Sección de métrica principal
 * - Fila de estadísticas
 * - Fila de advertencia
 * - Link de "Ver detalles"
 * 
 * @param {Object} props - Props del componente
 * @param {string} props.title - Título de la card
 * @param {string} props.subtitle - Subtítulo (generalmente período: mes/año)
 * @param {ReactNode} props.mainMetric - Contenido de la métrica principal
 * @param {ReactNode} props.statsRow - Contenido de la fila de estadísticas
 * @param {ReactNode} props.warningRow - Contenido de la fila de advertencia
 * @param {function} props.onEdit - Callback cuando se hace click en el icono de edición (opcional)
 * @param {function} props.onViewDetails - Callback cuando se hace click en "Ver detalles"
 * @param {string} props.detailsLinkText - Texto del link de detalles (default: "Ver detalles")
 * @returns {JSX.Element} Tarjeta de métricas
 * 
 * @example
 * <MetricCard
 *   title="Implementación"
 *   subtitle="Junio 2025"
 *   mainMetric={<>365 Usuarios medidos</>}
 *   statsRow={<>73% Implementación</>}
 *   warningRow={<>135 sin mediciones</>}
 *   onViewDetails={() => console.log('Ver detalles')}
 * />
 */
export default function MetricCard({
    title,
    subtitle,
    mainMetric,
    statsRow,
    warningRow,
    onEdit,
    onViewDetails,
    detailsLinkText = 'Ver detalles',
}) {
    const classes = useCardStyles();

    return (
        <Paper className={classes.card}>
            {/* Header con título, subtítulo e icono de edición */}
            <Box className={classes.header}>
                <Box>
                    <Typography className={classes.title}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography className={classes.subtitle}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {onEdit && (
                    <MdOutlineEdit 
                        className={classes.iconButton} 
                        size={20}
                        onClick={onEdit}
                    />
                )}
            </Box>

            {/* Métrica principal */}
            {mainMetric && (
                <Box className={classes.mainMetric}>
                    {mainMetric}
                </Box>
            )}

            {/* Fila de estadísticas */}
            {statsRow && (
                <Box className={classes.statsRow}>
                    {statsRow}
                </Box>
            )}

            {/* Fila de advertencia */}
            {warningRow && (
                <Box className={classes.warningRow}>
                    {warningRow}
                </Box>
            )}

            {/* Link de ver detalles */}
            {onViewDetails && (
                <Box className={classes.detailsLink}>
                    <Link 
                        component="button" 
                        variant="body2" 
                        onClick={onViewDetails}
                        style={{ cursor: 'pointer' }}
                    >
                        {detailsLinkText}
                    </Link>
                </Box>
            )}
        </Paper>
    );
}

MetricCard.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    mainMetric: PropTypes.node,
    statsRow: PropTypes.node,
    warningRow: PropTypes.node,
    onEdit: PropTypes.func,
    onViewDetails: PropTypes.func,
    detailsLinkText: PropTypes.string,
};
